import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "reactstrap";
import { message } from "antd";
import exportFromJSON from "export-from-json";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { useSequenceGate } from "../../../components/upload/shared/SequenceGate";
import {
  taskBoqUploadPreviewColumns,
  TASK_BOQ_UPLOAD_TABLE_WIDTH,
  taskBudgetUploadPreviewColumns,
  TASK_BUDGET_UPLOAD_TABLE_WIDTH,
  taskResourceUploadPreviewColumns,
  TASK_RESOURCE_UPLOAD_TABLE_WIDTH,
  taskToolUploadPreviewColumns,
  TASK_TOOL_UPLOAD_TABLE_WIDTH,
} from "../../../components/upload/shared/previewColumns/taskUploadPreviewColumns";
import { projectsApis } from "../../../api/projects";
import { tasksApis } from "../../../api/NewTasks";
import { uploadApis } from "../../../api/Uploads";

// Parameterized wrapper over the shared ExcelUploadStageModal (upload →
// validating → preview → committing → done | failed) for the four task-level
// bulk uploads: task-boq, task-budget, task-resource, task-tool. Companion to
// WbsUploadModal.tsx — one file because the four types differ only in
// template URL, upload API, reference exports, preview columns and copy.
// Replaces the legacy fire-and-forget BulkUpload flow for these module keys
// (BulkUpload.tsx itself stays untouched for its remaining consumer).
//
// Note: like WBS, these types land validation-with-errors as status 'Error'
// on the backend, so error rows surface in the shared modal's *failed* stage
// rather than a mixed preview. Their preview rows carry taskName but no
// excelRow, so failed-stage bullets are labeled by task name (or array
// position, valid for page 1).

export type TaskUploadKey =
  | "task-boq"
  | "task-budget"
  | "task-resource"
  | "task-tool";

const blobUrl: String = process.env.REACT_APP_BLOB_URL || "";
const blobContainer: String = process.env.REACT_APP_BLOB_CONTAINER || "";

const TASK_UPLOAD_REQUIRED_MODULES = ["Phases", "Activities"];

interface TaskUploadTypeDef {
  /** Modal title, from the legacy BulkUpload Configuration map. */
  title: string;
  entityLabel: string;
  /** Exact legacy blob template URL. */
  templateUrl: string;
  startUpload: (formData: FormData) => Promise<any>;
  /** Reference CSV export links shown in the intro (legacy references list). */
  references: string[];
  previewColumns: any[];
  previewScrollX: number;
  copy: ExcelUploadTypeConfig["copy"];
  /** Type-specific note block under the reference links (task-boq auto-calc). */
  note: React.ReactNode;
}

// Generic mandatory-fields note (all types except task-boq), copied from
// BulkUpload's ReferenceSection.
const mandatoryFieldsNote = (
  <p>
    <strong>Note:</strong> All fields in the template are mandatory. Each one
    must have unique information to avoid errors during the import process.
  </p>
);

// task-boq auto-calc note, copied from BulkUpload's ReferenceSection.
const boqAutoCalcNote = (
  <p>
    <strong>Note:</strong> The task-level quantity is calculated automatically
    from Material QTY — do not compute it yourself. For tasks that have an
    Output Basis (see the Tasks reference sheet), OutputQty and OutputUom are
    mandatory and must match the task exactly; Material QTY is then the
    material needed per Output Basis. For tasks without an Output Basis, leave
    OutputQty and OutputUom empty — Material QTY is used as the full task
    quantity. Material UOM must match the BOQ line's UOM (see the BOQ
    reference sheet).
  </p>
);

const TASK_UPLOAD_TYPES: Record<TaskUploadKey, TaskUploadTypeDef> = {
  "task-boq": {
    title: "Bulk BOQ Creation",
    entityLabel: "Task BOQ",
    templateUrl: `${blobUrl}${blobContainer}/task-boq-creation-template-v2.xlsx`,
    startUpload: (formData: FormData) => tasksApis.uploadTaskBOQ(formData),
    references: ["Tasks", "BOQ"],
    previewColumns: taskBoqUploadPreviewColumns,
    previewScrollX: TASK_BOQ_UPLOAD_TABLE_WIDTH,
    copy: {
      uploadIntro: (
        <p>
          You're about to allocate BOQ line materials to tasks. Fill the BOQ
          Excel template accurately and completely, then upload it below.
          You'll see a full preview before anything is saved.
        </p>
      ),
      validatingHeading: "Validating your file…",
      validatingSub:
        "Checking tasks, BOQ lines, materials, quantities and norms.",
      committingHeading: "Creating BOQ lines…",
      committingSub: "Allocating BOQ line materials to tasks.",
      commitCta: "Create BOQ Lines",
      doneHeading: "BOQ lines created successfully",
      doneSub: (report: UploadReportState) => (
        <>
          {report.successCount} BOQ line(s) allocated. The material
          allocations are now visible on the tasks.
        </>
      ),
      creationFailedNote:
        "Creation failed — no BOQ lines were saved (the batch is all-or-nothing).",
    },
    note: boqAutoCalcNote,
  },
  "task-budget": {
    title: "Bulk Budget Creation",
    entityLabel: "Task Budget",
    templateUrl: `${blobUrl}${blobContainer}/Task-budget-creation-template.xlsx`,
    startUpload: (formData: FormData) => tasksApis.uploadTaskBudget(formData),
    references: ["Tasks", "Budget"],
    previewColumns: taskBudgetUploadPreviewColumns,
    previewScrollX: TASK_BUDGET_UPLOAD_TABLE_WIDTH,
    copy: {
      uploadIntro: (
        <p>
          You're about to allocate budgets to tasks. Fill the Budget Excel
          template accurately and completely, then upload it below. You'll see
          a full preview before anything is saved.
        </p>
      ),
      validatingHeading: "Validating your file…",
      validatingSub: "Checking tasks, budget lines and allocation amounts.",
      committingHeading: "Creating budgets…",
      committingSub: "Allocating budget line amounts to tasks.",
      commitCta: "Create Budgets",
      doneHeading: "Budgets created successfully",
      doneSub: (report: UploadReportState) => (
        <>
          {report.successCount} budget allocation(s) created. The allocations
          are now visible on the tasks.
        </>
      ),
      creationFailedNote:
        "Creation failed — no budget allocations were saved (the batch is all-or-nothing).",
    },
    note: mandatoryFieldsNote,
  },
  "task-resource": {
    title: "Bulk Resource Creation",
    entityLabel: "Task Resource",
    templateUrl: `${blobUrl}${blobContainer}/task-resource-creation-template.xlsx`,
    startUpload: (formData: FormData) => tasksApis.uploadTaskResource(formData),
    references: ["Tasks", "Labor Types", "Labor Categories", "Work UOMs"],
    previewColumns: taskResourceUploadPreviewColumns,
    previewScrollX: TASK_RESOURCE_UPLOAD_TABLE_WIDTH,
    copy: {
      uploadIntro: (
        <p>
          You're about to add labor resources to tasks. Fill the Resource
          Excel template accurately and completely, then upload it below.
          You'll see a full preview before anything is saved.
        </p>
      ),
      validatingHeading: "Validating your file…",
      validatingSub:
        "Checking tasks, labor types, labor categories, counts and rates.",
      committingHeading: "Creating resources…",
      committingSub: "Adding labor resources to tasks.",
      commitCta: "Create Resources",
      doneHeading: "Resources created successfully",
      doneSub: (report: UploadReportState) => (
        <>
          {report.successCount} resource(s) created. The labor resources are
          now visible on the tasks.
        </>
      ),
      creationFailedNote:
        "Creation failed — no resources were saved (the batch is all-or-nothing).",
    },
    note: mandatoryFieldsNote,
  },
  "task-tool": {
    title: "Bulk Tools & Machinery Creation",
    entityLabel: "Task Tools & Machinery",
    templateUrl: `${blobUrl}${blobContainer}/task-tool-creation-template.xlsx`,
    startUpload: (formData: FormData) => tasksApis.uploadTaskTool(formData),
    references: ["Tasks", "Tools & Machineries", "Work UOMs"],
    previewColumns: taskToolUploadPreviewColumns,
    previewScrollX: TASK_TOOL_UPLOAD_TABLE_WIDTH,
    copy: {
      uploadIntro: (
        <p>
          You're about to add tools &amp; machinery to tasks. Fill the Tools
          &amp; Machinery Excel template accurately and completely, then
          upload it below. You'll see a full preview before anything is saved.
        </p>
      ),
      validatingHeading: "Validating your file…",
      validatingSub:
        "Checking tasks, tools & machinery, serial numbers and rates.",
      committingHeading: "Creating tools & machinery…",
      committingSub: "Adding tools & machinery to tasks.",
      commitCta: "Create Tools",
      doneHeading: "Tools & machinery created successfully",
      doneSub: (report: UploadReportState) => (
        <>
          {report.successCount} tool(s) created. The tools &amp; machinery are
          now visible on the tasks.
        </>
      ),
      creationFailedNote:
        "Creation failed — no tools were saved (the batch is all-or-nothing).",
    },
    note: mandatoryFieldsNote,
  },
};

// Reference-data block for the upload stage: per-type master-data CSV export
// links + note. Fetch functions are COPIED from the matching moduleKey
// branches of src/components/upload/BulkUpload.tsx (which keeps its own
// copies untouched): task-boq fetches tasks + BOQs, task-budget tasks +
// budgets, task-resource / task-tool everything from the single
// /excel-upload/masterData endpoint. The component is only mounted once the
// sequence gate allows the form, so the fetches are effectively lazy —
// mirroring BulkUpload's `fetchedRefs && isUploadAllowed` behavior.
const TaskTypeUploadIntro = ({
  uploadKey,
  projectId,
}: {
  uploadKey: TaskUploadKey;
  projectId?: string;
}) => {
  const [tasks, setTasks] = useState<any>([]);
  const [boqs, setBoqs] = useState<any>([]);
  const [budgets, setBudgets] = useState<any>([]);
  const [laborTypes, setLaborTypes] = useState<any>([]);
  const [laborCategories, setLaborCategories] = useState<any>([]);
  const [toolsAndMachineries, setToolsAndMachineries] = useState<any>([]);
  const [workUoms, setWorkUoms] = useState<any>([]);
  const [fetchedRefs, setFetchedRefs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const typeDef = TASK_UPLOAD_TYPES[uploadKey];

  // Fetch tasks (WBS flattened to one row per task)
  const fetchTasks = async (projectId: string) => {
    try {
      setLoading(true);
      const {
        data,
        status,
        message: apiMessage,
      } = await projectsApis.fetchProjectWBS(projectId);

      if (status && data?.phases) {
        const taskOptions: any[] = [];

        // Process each phase
        data.phases.forEach((phase: any) => {
          if (phase.activities && Array.isArray(phase.activities)) {
            // Process each activity in the phase
            phase.activities.forEach((activity: any) => {
              if (activity.tasks && Array.isArray(activity.tasks)) {
                // Process each task in the activity
                activity.tasks.forEach((task: any) => {
                  taskOptions.push({
                    // Task details
                    TaskName: task.taskName,
                    TaskId: task.taskSequenceId,

                    // Activity details
                    ActivityName: activity.name,

                    // Phase details
                    PhaseName: phase.phaseName,

                    // Work/output basis — drives the BOQ upload auto-calc:
                    // tasks with an Output Basis need OutputQty/OutputUom
                    // in the sheet and get MaterialQTY scaled by WorkQty
                    WorkType: task.workType,
                    WorkQty: task.workQuantity,
                    WorkUOM: task.workUom,
                    OutputQty: task.outputQuantity,
                    OutputUom: task.outputBasisUom,
                  });
                });
              }
            });
          }
        });

        setTasks(taskOptions);
      } else {
        console.error("No phases data found in response");
        messageApi.error(apiMessage || "No task data available");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      messageApi.error("Failed to fetch tasks from server");
    } finally {
      setLoading(false);
    }
  };

  // Fetch BOQs with materials (one row per BOQ line + material)
  const fetchProjectBoqs = async (projectId: string) => {
    try {
      const { data, status } = await projectsApis.fetchProjectBoqs(projectId);
      if (status) {
        const boqOptions: any[] = [];
        data.forEach((boq: any) => {
          (boq.lines || []).forEach((line: any) => {
            boqOptions.push({
              BOQName: boq.boqTitle,
              BOQId: boq.boqTargetId,
              BOQLineName: line.boqLineName,
              BOQLineId: line.boqLineId ?? line.targetLineId,
              ContractorName: line.contractorName,
              SiteStructureUnitId: line.siteStructureUnitId,
              "Tower/Villa": line.siteStructureUnitName,
              SiteStructureSubUnitId: line.siteStructureSubUnitId,
              "Flat/Shop": line.siteStructureSubUnitName,
              Floor: line.floorNo,
              LocationId: line.locationId,
              LocationName: line.locationName,
              MaterialName: line.materialName,
              MaterialId: line.materialId,
              MaterialUOM: line.uomName,
            });
          });
        });
        setBoqs(boqOptions);
      }
    } catch (error) {
      console.error("Error fetching project BOQs:", error);
      messageApi.error("Failed to fetch BOQs");
    }
  };

  // Fetch budgets
  const fetchBudgets = async (projectId: string) => {
    try {
      const { data, status } = await projectsApis.fetchBudgetLines(projectId);
      if (status) {
        const budgetOptions = data.map((budget: any) => ({
          BudgetId: budget.headerId,
          BudgetName: budget.budgetId,
          BudgetLineId: budget.lineId,
          BudgetLineName: budget.budgetLineName,
          BudgetCategory: budget.budgetCategory,
        }));
        setBudgets(budgetOptions);
      }
    } catch (error) {
      console.error("Error fetching budgets:", error);
      messageApi.error("Failed to fetch budgets");
    }
  };

  // Resource & tool uploads pull every reference they need (tasks, labor masters,
  // tools, work UOMs) from one endpoint, scoped to the project and the template.
  // Sections not used by the uploadType come back absent, so default each to [].
  const fetchBulkUploadMasterData = async (
    projectId: string,
    uploadType: "task-resource-upload" | "task-tool-upload"
  ) => {
    try {
      setLoading(true);
      const { data, status, message: apiMessage } =
        await uploadApis.fetchBulkUploadMasterData(projectId, uploadType);

      if (!status) {
        messageApi.error(apiMessage || "Failed to fetch reference data");
        return;
      }

      setTasks(
        (data?.tasks ?? []).map((task: any) => ({
          TaskId: task?.taskSequenceId,
          TaskName: task?.taskName,
          PhaseName: task?.phaseName,
          ActivityName: task?.activityName,
          WorkType: task?.workType,
          WorkQuantity: task?.workQuantity,
          OutputQuantity: task?.outputQuantity,
        }))
      );

      setLaborTypes(
        (data?.laborTypes ?? []).map((item: any) => ({
          LaborTypeId: item?.laborTypeId,
          LaborTypeName: item?.laborTypeName,
        }))
      );

      setLaborCategories(
        (data?.laborCategories ?? []).map((item: any) => ({
          LaborCategoryId: item?.laborCategoryId,
          LaborCategoryName: item?.laborCategoryName,
        }))
      );

      setToolsAndMachineries(
        (data?.tools ?? []).map((item: any) => ({
          ToolId: item?.toolId,
          ToolName: item?.toolName,
          ToolType: item?.toolType,
        }))
      );

      setWorkUoms(
        (data?.workUoms ?? []).map((item: any) => ({
          WorkUOMId: item?.workUomId,
          WorkUOMName: item?.uomName,
        }))
      );
    } catch (error) {
      console.error("Error fetching bulk upload master data:", error);
      messageApi.error("Failed to fetch reference data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRefs || !projectId) return;
    // same fetch set as BulkUpload's per-moduleKey branches
    if (uploadKey === "task-boq") {
      fetchTasks(projectId);
      fetchProjectBoqs(projectId);
    } else if (uploadKey === "task-budget") {
      fetchTasks(projectId);
      fetchBudgets(projectId);
    } else if (uploadKey === "task-resource") {
      fetchBulkUploadMasterData(projectId, "task-resource-upload");
    } else if (uploadKey === "task-tool") {
      fetchBulkUploadMasterData(projectId, "task-tool-upload");
    }
    // prevent repeat fetch on re-render (component remounts on each modal open)
    setFetchedRefs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedRefs, projectId, uploadKey]);

  const referenceData: { [key: string]: any } = useMemo(
    () => ({
      Tasks: tasks,
      BOQ: boqs,
      Budget: budgets,
      "Labor Types": laborTypes,
      "Labor Categories": laborCategories,
      "Tools & Machineries": toolsAndMachineries,
      "Work UOMs": workUoms,
    }),
    [
      tasks,
      boqs,
      budgets,
      laborTypes,
      laborCategories,
      toolsAndMachineries,
      workUoms,
    ]
  );

  const referenceFileNames: { [key: string]: string } = useMemo(
    () => ({
      Tasks: `Tasks_Reference_${projectId || "All"}`,
      BOQ: `BOQs_${projectId || "All"}`,
      Budget: `Budgets_${projectId || "All"}`,
      "Labor Types": "LaborTypes",
      "Labor Categories": "LaborCategories",
      "Tools & Machineries": "ToolsAndMachineries",
      "Work UOMs": "WorkUOMs",
    }),
    [projectId]
  );

  const handleExportReference = useCallback(
    (ref: string) => {
      const exportType = exportFromJSON.types.csv;
      exportFromJSON({
        data: referenceData[ref],
        fileName: referenceFileNames[ref],
        exportType,
      });
    },
    [referenceData, referenceFileNames]
  );

  const referenceLinks = useMemo(
    () =>
      typeDef.references.map((ref: string, index: number) => (
        <React.Fragment key={ref}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleExportReference(ref);
            }}
            target="_self"
            style={{ textDecoration: "underline" }}
          >
            {ref}
          </a>
          {index < typeDef.references.length - 1 && ", "}
        </React.Fragment>
      )),
    [typeDef.references, handleExportReference]
  );

  return (
    <>
      {contextHolder}
      <p className="mt-2 mb-0">
        You can check {referenceLinks} for reference
        {loading && <Spinner size="sm" className="ms-2" />}
      </p>
      {typeDef.note}
    </>
  );
};

const TaskTypeUploadModal = ({
  uploadKey,
  projectId,
  projectName,
  projectSiteName,
  projectSiteId,
  open,
  onClose,
  onSuccess,
}: {
  uploadKey: TaskUploadKey;
  projectId?: string;
  projectName?: string;
  projectSiteName?: string;
  projectSiteId?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const gate = useSequenceGate({
    projectId,
    requiredModules: TASK_UPLOAD_REQUIRED_MODULES,
    projectName,
    projectSiteName,
    projectSiteId,
  });

  const config: ExcelUploadTypeConfig = useMemo(() => {
    const typeDef = TASK_UPLOAD_TYPES[uploadKey];
    return {
      title: typeDef.title,
      entityLabel: typeDef.entityLabel,
      templateSource: { kind: "blob", url: typeDef.templateUrl },
      startUpload: typeDef.startUpload,
      buildBody: (comments: string) => ({
        projectId,
        comment: comments ? comments : "N/A",
      }),
      previewColumns: typeDef.previewColumns,
      previewScrollX: typeDef.previewScrollX,
      // Preview rows carry no excelRow field (backend pick() list) — fall
      // back to the task name, then array position; row n on page 1 = Excel
      // row n+2.
      rowErrorLabel: (row: any, index: number) =>
        row?.taskName || `Row ${index + 2}`,
      copy: typeDef.copy,
      gate,
      introSection: (
        // key: one modal instance serves all four types and antd keeps the
        // modal children mounted, so remount the intro (and its fetched
        // reference state) whenever the type changes.
        <TaskTypeUploadIntro
          key={uploadKey}
          uploadKey={uploadKey}
          projectId={projectId}
        />
      ),
      discardOnCancelFromPreview: true,
    };
  }, [uploadKey, projectId, gate]);

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default TaskTypeUploadModal;
